import { Carousel } from 'antd';
import Colors from 'enums/Colors';
import styled from 'styled-components';
import becoGrain from '../../../../sennaBeco/assets/grain.png';

/*
 * Tamanho de cada logo da régua de patrocinadores, em px do design: "row" no layout 1920x682 (linha),
 * "column" no layout 1440x750 (coluna; os layouts de tablet e mobile usam a mesma coluna em escala menor).
 */
const becoSponsorSizes = {
    rouanet: { row: 'width: calc(109 * var(--s));', column: 'width: calc(129 * var(--s));' },
    itau: { row: 'height: calc(33 * var(--s));', column: 'height: calc(38 * var(--s));' },
    alvarezMarsal: { row: 'height: calc(35 * var(--s));', column: 'height: calc(42 * var(--s));' },
    shp: { row: 'width: calc(60 * var(--s));', column: 'width: calc(74 * var(--s));' },
    senna: { row: 'width: calc(67 * var(--s));', column: 'width: calc(80 * var(--s));' },
    instituto: { row: 'width: calc(59 * var(--s));', column: 'width: calc(69 * var(--s));' },
    minc: { row: 'width: calc(121 * var(--s));', column: 'width: calc(130 * var(--s));' },
};

export type BecoSponsorLogoName = keyof typeof becoSponsorSizes;

const StyleCarouselHome = {
    Container: styled.div`
        position: relative;
        padding-top: 86px;

        @media screen and (max-width: 700px) {
            padding-top: 0;
        }
    `,

    ContainerNav: styled.div`
        position: absolute;
        width: 100%;
        bottom: 9px;
        height: 190px;
        display: flex;
        align-items: center;

        @media screen and (max-width: 1000px) {
            height: 160px;
        }

        @media screen and (max-width: 680px) {
            height: 80px;
        }
    `,

    ContentNav: styled.div`
        display: flex;
        justify-content: center;
        flex-direction: column;
        width: 100%;
        height: 100%;
        color: ${Colors.White};
        padding-left: 80px;
        padding-right: 80px;
        z-index: 2;

        @media screen and (max-width: 1300px) {
            padding-left: 24px;
            padding-right: 24px;
        }
        @media screen and (max-width: 680px) {
            align-items: center;
            padding-left: 0px;
            padding-right: 0px;
        }
    `,

    ContentInfo: styled.div`
        display: flex;
        align-items: center;
        gap: 1rem;
        width: 100%;
        height: 80%;

        @media screen and (min-width: 1400px) {
            gap: 3rem;
        }

        @media screen and (max-width: 680px) {
            display: none;
        }
    `,

    ContentCarousel: styled.div<{
        img: string;
        beco?: boolean;
        becoMobile?: string;
        rightPorcent?: string;
        topPorcent?: string;
        revert?: boolean;
    }>`
        cursor: pointer;
        margin: 0;
        height: 682px;
        width: 100%;

        &:before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;

            background-image: ${({ revert, beco, img }) =>
                beco
                    ? `url(${img}), url(${becoGrain})`
                    : revert
                    ? `linear-gradient(
                    115deg,
                    rgba(0,0,0,0.4009978991596639) 0%,
                    rgba(47,47,47,0.5102415966386555) 40%,
                    rgba(8,8,8,0.7931547619047619) 100%
                ), url(${img})`
                    : `linear-gradient(
                    115deg,
                    rgba(0, 0, 0, 0.7567401960784313) 3%,
                    rgba(47, 47, 47, 0.5326505602240896) 37%,
                    rgba(128, 128, 128, 0) 100%
                ), url(${img})`};

            background-size: cover;
            background-position: ${({ rightPorcent, topPorcent }) => (rightPorcent ? rightPorcent : '50%') + (topPorcent ? topPorcent : '40%')};
            background-repeat: no-repeat;

            transform: ${({ revert }) => revert && 'scaleX(-1)'};
            z-index: -1;

            /*
             * Banner Beco: a arte (Beco_poster.webp, recortada rente à figura, com a figura centralizada na imagem) é um
             * WebP com fundo transparente; os logos, a classificação indicativa e os textos são HTML (BecoSponsors, BecoRating, BodyBannerBeco).
             * A partir de 768px a figura ocupa toda a altura do slide, do topo até a base, passando por trás da barra
             * semitransparente "Navegue pelas Categorias", e fica sempre centralizada na horizontal. A textura (grain) fica atrás da arte, preenchendo todo o slide.
             * O grain é o mesmo da página do filme (SennaBeco.styled: Page), com a mesma cor de fundo e a mesma escala (12.66vw),
             * para que a textura seja idêntica nas duas telas em qualquer resolução.
             */
            ${({ beco }) =>
                beco &&
                `
                background-color: #eae8e7;
                background-size: auto var(--beco-poster-h), 12.66vw 12.66vw;
                background-position: center top, 0 0;
                background-repeat: no-repeat, repeat;
            `}

            /*
             * Mobile (layout 430x687): a arte fica embaixo, ao lado da coluna de logos, então usa a imagem do layout mobile.
             * Centralizada, mas sem deixar os rótulos dos logos (x = 37/687 da altura da arte) a menos de 16px da borda:
             * em telas mais estreitas que a arte (~430px) o corte fica no lado da cabeça, não nos logos.
             */
            @media screen and (max-width: 767px) {
                ${({ beco, becoMobile, img }) =>
                    beco &&
                    `
                    background-image: url(${becoMobile || img}), url(${becoGrain});
                    background-size: auto var(--beco-art-h), 12.66vw 12.66vw;
                    background-position:
                        max(calc(16px - var(--beco-art-h) * 0.0539), 50%) top,
                        0 0;
                `}
            }
        }

        /*
         * --beco-height: os layouts de 1440, 1032, 960 e 768 têm todos 750px de altura.
         * --beco-nav-height: distância entre o topo do ContainerNav e a base do slide (medida no navegador): 191px / 161px no desktop e tablet;
         * no mobile os dots ocupam mais altura e empurram a barra para baixo, então fica 146px (681–767px) e 66px (até 680px).
         * --beco-poster-h: altura da figura (Beco_poster.webp) a partir de 768px: o slide inteiro (a barra fica por cima).
         * --beco-art-ratio: altura da imagem do layout / base da arte na imagem; --beco-design-h: altura do layout no design.
         * --beco-art-h: altura que a imagem do layout teria na tela; --beco-k: escala entre o design e a tela (1px do design = --beco-k na tela),
         * usada para posicionar e dimensionar os logos, a classificação e os textos (no mobile, também a arte) nas coordenadas do design.
         * --beco-figure-edge: distância da borda esquerda da figura até o centro, na altura do texto, em proporção da altura da figura
         * (Beco_poster.webp: 0.296).
         */
        ${({ beco }) =>
            beco &&
            `
            --beco-figure-edge: 0.296;
            --beco-height: 682px;
            --beco-nav-height: 191px;
            --beco-art-ratio: 1.386; /* 682 / 492 */
            --beco-design-h: 682;
            --beco-art-h: calc((var(--beco-height) - var(--beco-nav-height)) * var(--beco-art-ratio));
            --beco-k: calc(var(--beco-art-h) / var(--beco-design-h));
            --beco-poster-h: var(--beco-height);
            height: var(--beco-height);
        `}

        @media screen and (max-width: 1799px) {
            ${({ beco }) =>
                beco &&
                `
                --beco-height: 750px;
                --beco-art-ratio: 1.234; /* 750 / 608 */
                --beco-design-h: 750;
            `}
        }

        @media screen and (max-width: 1199px) {
            ${({ beco }) =>
                beco &&
                `
                --beco-art-ratio: 1.236; /* 750 / 607 (mesmo valor para 1032, 960 e 768) */
            `}
        }

        @media screen and (max-width: 1000px) {
            ${({ beco }) =>
                beco &&
                `
                --beco-nav-height: 161px;
            `}
        }

        @media screen and (max-width: 767px) {
            ${({ beco }) =>
                beco &&
                `
                --beco-height: 667px;
                --beco-nav-height: 146px;
                --beco-art-ratio: 1.147; /* 687 / 599 (layout 430x687) */
                --beco-design-h: 687;
            `}
        }

        @media screen and (max-width: 680px) {
            ${({ beco }) =>
                beco &&
                `
                --beco-nav-height: 66px;
            `}
        }

        @media screen and (min-width: 1919px) {
            ${({ beco }) =>
                !beco &&
                `
                background-position: center 40%;
            `}
        }

        @media screen and (min-width: 2500px) {
            ${({ beco }) =>
                !beco &&
                `
                background-position: center 20%;
            `}
        }
    `,

    ContainerMenuNav: styled.div`
        display: flex;
        flex-direction: column;
        width: 200px;
        height: 100%;
        gap: 0.5rem;
        transition: all 0.3s ease;
        cursor: pointer;

        &:hover {
            h1,
            p {
                transform: translateY(-10px); /* Move o texto para cima */
            }

            h1 {
                color: ${Colors.Yellow};
            }

            span {
                opacity: 1; /* Torna o botão visível */
                transform: translateY(0); /* Move o botão para a posição original */
                color: ${Colors.Yellow};

                @media screen and (max-width: 1550px) {
                    transform: translateY(-22px);
                }
            }
        }

        h1,
        p {
            margin: 0;
            transition: transform 0.3s ease; /* Suaviza o movimento */
        }

        span {
            transform: translateY(20px); /* Começa fora da tela, movendo-o para baixo */
            opacity: 0; /* Começa invisível */
            transition: all 0.3s ease; /* Suaviza a transição para visibilidade e posição */
        }
    `,

    ContainerDots: styled.div`
        position: relative;
        display: flex;
        justify-content: center;
        align-items: center;
        text-align: center;
        bottom: 210px;

        @media screen and (max-width: 1000px) {
            bottom: 180px;
        }

        @media screen and (max-width: 680px) {
            bottom: 100px;
        }
    `,

    ContentItemNav: styled.div`
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-top: 10px;
        width: 100%;

        @media screen and (max-width: 680px) {
            justify-content: center;
            flex-direction: column;
        }
    `,

    BodyBanner: styled.div`
        height: 100%;
        display: flex;
        flex-direction: column;
        gap: 1rem;
        margin-top: 143px;
        margin-left: 80px;
        position: relative;

        @media screen and (max-width: 1300px) {
            margin-left: 20px;
        }

        @media screen and (max-width: 900px) {
            margin-top: 240px;
        }
        @media screen and (max-width: 670px) {
            margin-top: 240px;
        }
        @media screen and (max-width: 450px) {
            gap: 0.2rem;
        }
    `,

    BodyBannerBeco: styled.div`
        height: 100%;
        display: flex;
        flex-direction: column;
        gap: 1rem;
        margin-top: 37px;
        margin-left: 98px;
        position: relative;
        z-index: 1;

        @media screen and (max-width: 1799px) {
            margin-top: 40px;
            margin-left: 60px;
            /* o texto quebra antes da figura centralizada (ver a largura no bloco de tablet abaixo) */
            max-width: calc(50% - var(--beco-figure-edge) * var(--beco-poster-h) - 60px - 16px);
        }

        @media screen and (max-width: 1199px) {
            max-width: none;
            position: absolute;
            top: 40px;
            left: clamp(40px, 5vw, 60px);
            /*
             * a figura é centralizada e, na altura do texto, a borda esquerda dela fica a --beco-figure-edge * altura da figura do centro:
             * o texto quebra em mais linhas para caber ao lado.
             */
            width: min(310px, calc(50% - var(--beco-figure-edge) * var(--beco-poster-h) - clamp(40px, 5vw, 60px) - 16px));
            height: auto;
            margin: 0;
            transform: none;
            align-items: flex-start;
            text-align: left;
            gap: 0.8rem;
        }

        /*
         * Mobile (layout 430x687): o texto acompanha a arte, que fica centralizada e tem a altura calculada no ContentCarousel.
         * --beco-art-h = altura renderizada da imagem 430x687; os rótulos dos logos começam em x = 37/687 da altura da arte
         * (metade da largura = 0.313) e o "Lei Rouanet" em 298/687, então o bloco fica alinhado aos logos e termina logo acima deles.
         */
        @media screen and (max-width: 767px) {
            top: calc(var(--beco-art-h) * 0.42);
            left: max(16px, calc(50% - var(--beco-art-h) * 0.2591));
            width: min(320px, calc(100% - 32px));
            transform: translateY(-100%);
            align-items: flex-start;
            text-align: left;
            gap: 12px;
        }
    `,

    TitleBannerBeco: styled.h1`
        position: relative;
        margin: 0;
        max-width: 650px;

        font-family: 'Barlow';
        font-size: 48px;
        font-weight: 700;
        line-height: 62.4px;
        letter-spacing: -0.01em;
        text-align: left;

        color: #616161;

        @media screen and (max-width: 1799px) {
            font-size: 40px;
            line-height: 50px;
            max-width: 470px;
        }

        @media screen and (max-width: 1199px) {
            font-size: 32px;
            line-height: 40px;
            max-width: 310px;
            text-align: left;
        }

        @media screen and (max-width: 959px) {
            font-size: 26px;
            line-height: 32px;
            max-width: 220px;
        }

        @media screen and (max-width: 767px) {
            font-size: 22px;
            line-height: 30px;
            max-width: 320px;
            text-align: left;
        }

        @media screen and (max-width: 374px) {
            font-size: 20px;
            line-height: 28px;
        }
    `,

    TitleBannerBecoHighlight: styled.span`
        color: #9d7544;
    `,

    DescriptionBannerBeco: styled.p`
        position: relative;
        max-width: 500px;
        margin: 0;

        font-family: 'Barlow';
        font-size: 18px;
        font-weight: 400;
        line-height: 27px;
        text-align: left;

        color: #616161;

        @media screen and (max-width: 1799px) {
            font-size: 16px;
            line-height: 24px;
            max-width: 420px;
        }

        @media screen and (max-width: 1199px) {
            font-size: 14px;
            line-height: 21px;
            max-width: 300px;
            text-align: left;
        }

        @media screen and (max-width: 959px) {
            font-size: 13px;
            line-height: 19px;
            max-width: 210px;
        }

        @media screen and (max-width: 767px) {
            font-size: 14px;
            line-height: 22px;
            max-width: 320px;
            text-align: left;
        }
    `,

    /*
     * O círculo do play fica concêntrico com a ponta arredondada do botão: o padding da direita é igual à folga
     * de cima e de baixo, (altura - diâmetro do círculo) / 2.
     */
    ButtonBeco: styled.button`
        width: 236px;
        height: 56px;
        margin-top: 80px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0 8px 0 33px;

        border: none;
        border-radius: 34px;
        background: #9d7644;
        color: #ffffff;

        font-family: 'Barlow', sans-serif;
        font-size: 20px;
        font-weight: 500;
        line-height: 1;

        cursor: pointer;
        transition: opacity 0.2s ease;

        &:hover {
            opacity: 0.9;
        }

        @media screen and (max-width: 1199px) {
            margin-top: 0;
            width: 180px;
            height: 48px;
            padding-right: 7px;
            padding-left: 18px;
            border-radius: 24px;
            font-size: 16px;
        }

        @media screen and (max-width: 767px) {
            width: 146px;
            height: 38px;
            padding-right: 5px;
            border-radius: 34px;
            font-size: 14px;
        }
    `,

    ButtonBecoText: styled.span`
        padding-bottom: 5px;

        @media screen and (max-width: 767px) {
            padding-bottom: 1px;
        }
    `,

    // O triângulo fica ~1px à direita do centro (padding-left 2px) para parecer centralizado no círculo.
    ButtonBecoIcon: styled.span`
        width: 40px;
        height: 40px;
        padding-left: 2px;
        display: flex;
        align-items: center;
        justify-content: center;

        flex-shrink: 0;
        border-radius: 50%;
        background: #ffffff;

        img {
            width: 16px;
            height: 24px;
            display: block;
        }

        @media screen and (max-width: 1199px) {
            width: 34px;
            height: 34px;

            img {
                width: 14px;
                height: 21px;
            }
        }

        @media screen and (max-width: 767px) {
            width: 28px;
            height: 28px;
            padding-left: 1px;

            img {
                width: 11px;
                height: 16.5px;
            }
        }
    `,

    EyebrowBeco: styled.p`
        margin: 0 0 39px;

        font-family: 'Big Shoulders Display', sans-serif;
        font-size: 15px;
        font-weight: 700;
        line-height: 18px;
        letter-spacing: 0.005em;
        text-align: left;

        color: #4f4f4f;

        @media screen and (max-width: 1199px) {
            margin-bottom: 4px;
            font-size: 14px;
            line-height: 17px;
        }

        @media screen and (max-width: 767px) {
            margin-bottom: 0;
            font-size: 12px;
            line-height: 15px;
        }
    `,

    EyebrowBecoHighlight: styled.span`
        color: #9d7644;
    `,

    /*
     * Classificação indicativa: no desktop fica à direita, na altura do botão "Assista agora" (layout 1920x682);
     * nas demais resoluções, no canto inferior direito acima da barra "Navegue pelas Categorias"; no mobile, na altura do botão
     * (o BodyBannerBeco termina em 0.42 da arte e o botão tem 38px), alinhada à direita da imagem 430x687 (x = 386 do design)
     * sem passar de 16px da borda — o topo do slide fica atrás do header.
     */
    BecoRating: styled.img`
        position: absolute;
        right: 35px;
        bottom: calc(var(--beco-nav-height) + 106px);
        width: 41px;
        height: auto;

        @media screen and (max-width: 1799px) {
            right: 32px;
            bottom: calc(var(--beco-nav-height) + 32px);
            width: 36px;
        }

        @media screen and (max-width: 1199px) {
            right: 24px;
            bottom: calc(var(--beco-nav-height) + 24px);
            width: 32px;
        }

        @media screen and (max-width: 767px) {
            top: calc(var(--beco-art-h) * 0.42 - 33px);
            right: auto;
            left: min(calc(100% - 44px), calc(max(calc(16px - 37 * var(--beco-k)), calc(50% - 215 * var(--beco-k))) + 386 * var(--beco-k)));
            bottom: auto;
            width: 28px;
        }
    `,

    /*
     * Régua de patrocinadores. Nos layouts do design ela era parte da imagem; agora é HTML posicionado sobre a arte:
     * - a partir de 1800px: linha no canto superior direito, com tamanhos fixos (layout 1920x682);
     * - abaixo disso: coluna ao lado da arte, posicionada e dimensionada pela mesma escala da arte (--beco-k),
     *   nas coordenadas de cada layout do design (x a partir do centro da imagem, y a partir do topo).
     * --s: tamanho de 1px do design na tela.
     */
    BecoSponsors: styled.div`
        --s: 1px;
        position: absolute;
        top: 65px;
        right: 35px;
        display: flex;
        align-items: center;
        gap: calc(17 * var(--s));

        @media screen and (max-width: 1799px) {
            --s: var(--beco-k);
            top: calc(85.5 * var(--beco-k));
            right: auto;
            left: calc(50% + 447.5 * var(--beco-k));
            width: calc(130 * var(--s));
            flex-direction: column;
            align-items: stretch;
            gap: 0;
        }

        @media screen and (max-width: 1199px) {
            top: calc(74.7 * var(--beco-k));
            left: calc(50% + 319.6 * var(--beco-k));
        }

        @media screen and (max-width: 1031px) {
            left: calc(50% + 302.6 * var(--beco-k));
        }

        @media screen and (max-width: 959px) {
            --s: calc(0.74 * var(--beco-k));
            top: calc(66.8 * var(--beco-k));
            left: calc(50% + 245.1 * var(--beco-k));
        }

        /* mobile: a imagem não fica sempre centralizada (ver background-position do ContentCarousel) */
        @media screen and (max-width: 767px) {
            --s: calc(0.62 * var(--beco-k));
            top: calc(298.2 * var(--beco-k));
            left: calc(max(calc(16px - 37 * var(--beco-k)), calc(50% - 215 * var(--beco-k))) + 79.3 * var(--beco-k));
        }
    `,

    BecoSponsorGroup: styled.div`
        position: relative;
        display: flex;
        gap: calc(17 * var(--s));

        @media screen and (max-width: 1799px) {
            flex-direction: column;
            gap: 0;
        }
    `,

    BecoSponsorLabel: styled.span`
        position: absolute;
        bottom: calc(100% + 16 * var(--s));
        left: 0;

        font-family: 'Barlow', sans-serif;
        font-size: calc(10 * var(--s));
        font-weight: 700;
        line-height: 1;
        white-space: nowrap;

        color: #111111;

        @media screen and (max-width: 1799px) {
            top: calc(-6 * var(--s));
            bottom: auto;
            left: auto;
            right: calc(100% + 23 * var(--s));
            font-size: calc(11 * var(--s));
        }
    `,

    BecoSponsorCell: styled.div<{ separator?: boolean }>`
        --separator-color: #000000;
        height: calc(33 * var(--s));
        display: flex;
        align-items: center;
        justify-content: center;
        border-left: ${({ separator }) => (separator ? '1px solid var(--separator-color)' : 'none')};
        padding-left: ${({ separator }) => (separator ? 'calc(17 * var(--s))' : '0')};

        @media screen and (max-width: 1799px) {
            height: calc(64 * var(--s));
            border-left: none;
            border-top: ${({ separator }) => (separator ? '1px solid var(--separator-color)' : 'none')};
            padding-left: 0;
        }
    `,

    BecoSponsorLogo: styled.img<{ logo: BecoSponsorLogoName }>`
        display: block;
        ${({ logo }) => becoSponsorSizes[logo].row}

        @media screen and (max-width: 1799px) {
            ${({ logo }) => becoSponsorSizes[logo].column}
        }
    `,

    TextAuxTop: styled.p`
        position: absolute;
        font-family: 'Barlow';
        font-weight: 400;
        font-size: 12.41px;
        line-height: 150%;
        letter-spacing: -1%;
        vertical-align: middle;
        text-transform: uppercase;
        color: ${Colors.White};
        max-width: 425.38px;
        top: -100px;

        @media screen and (max-width: 630px) {
            top: -160px;
        }
    `,

    DestaqueTextBanner: styled.p`
        position: absolute;
        top: -10px;
        font-weight: 600;
        color: ${Colors.SoftBlue};
        text-transform: uppercase;
    `,

    TitleBanner: styled.h1`
        position: relative;
        font-family: 'Barlow';
        font-size: 48px;
        font-weight: 700;
        line-height: 62.4px;
        letter-spacing: -0.01em;
        text-align: left;
        color: ${Colors.White};

        @media screen and (max-width: 400px) {
            font-size: 42px;
        }
    `,

    DescriptionBanner: styled.p`
        position: relative;
        font-family: 'Barlow';
        font-size: 18px;
        font-weight: 400;
        line-height: 27px;
        text-align: left;
        max-width: 564px;
        color: ${Colors.White};
    `,

    TitleNav: styled.h1`
        font-family: 'Roboto';
        font-size: 20px;
        font-weight: 700;
        line-height: 32px;
        text-align: left;
    `,
    TitleNavStatic: styled.h2`
        font-family: 'Roboto';
        font-size: 20px;
        font-weight: 700;
        line-height: 32px;
        text-align: left;
    `,

    DescriptionNav: styled.p`
        font-family: 'Roboto';
        font-size: 16px;
        font-weight: 400;
        line-height: 24px;
        text-align: left;

        @media screen and (max-width: 1100px) {
            font-size: 14px;
        }
    `,

    SaibaMais: styled.span`
        font-family: 'Roboto';
        font-size: 16px;
        font-weight: 700;
        line-height: 32px;
    `,
};

export default StyleCarouselHome;
